import React, { useEffect, useMemo, useState } from "react";
import { firestore } from "../firebase";
import useAuth from "../hooks/useAuth";
import { generateQuotePdfClient } from "../utils/pdf";
import { openOrDownloadPdf } from "../utils/download";
import { formatPhoneNumber, formatINR } from "../utils/format";
import { calculateAdjustedPricing } from "../utils/priceAdjustment";
import TopBar from "./TopBar";
import Modal from "./Modal";
import { useModal } from "../hooks/useModal";
import { FiClock, FiCheck, FiX } from "react-icons/fi";
import "../styles/quotation-system.css";

export default function AdminQuotes() {
  React.useEffect(() => {
    const metaTag = document.createElement("meta");
    metaTag.name = "robots";
    metaTag.content = "noindex";
    document.head.appendChild(metaTag);
    document.body.classList.add('quotation-page');
    return () => {
      document.head.removeChild(metaTag);
      document.body.classList.remove('quotation-page');
    };
  }, []);

  const { user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [query, setQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [processingApproval, setProcessingApproval] = useState(null);
  const { modal, showAlert, showConfirm, hideModal } = useModal();

  useEffect(() => {
    // Load quotes
    const unsubQuotes = firestore
      .collection("quotes")
      .orderBy("meta.createdAt", "desc")
      .limit(200)
      .onSnapshot((snap) => {
        setQuotes(snap.docs.map((d) => d.data()));
      });

    // Load pending approvals
    const unsubApprovals = firestore
      .collection("priceAdjustmentRequests")
      .where("status", "==", "pending")
      .orderBy("requestedAt", "desc")
      .onSnapshot((snap) => {
        setPendingApprovals(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      });

    return () => {
      unsubQuotes();
      unsubApprovals();
    };
  }, [user]);

  const filtered = useMemo(() => {
    if (!query) return quotes;
    const q = query.toLowerCase();
    return quotes.filter(
      (x) =>
        x.meta?.quoteNo?.toLowerCase().includes(q) ||
        x.customer?.name?.toLowerCase().includes(q) ||
        x.customer?.phone?.toLowerCase().includes(q) ||
        x.meta?.salespersonName?.toLowerCase().includes(q)
    );
  }, [quotes, query]);

  const regenerate = async (quote) => {
    try {
      const { blob, filename } = await generateQuotePdfClient({ quote });
      // On desktop: open in new tab, on mobile: download
      openOrDownloadPdf(blob, filename);
    } catch (error) {
      console.error("Failed to generate PDF:", error);
      showAlert("Failed to generate PDF. Please try again.", "error", "Error");
    }
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    const allIds = filtered.map((q) => q.meta.id);
    const allSelected = allIds.every((id) => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : allIds);
  };

  const deleteSelected = async () => {
    if (!selectedIds.length) return;
    showConfirm(
      `Delete ${selectedIds.length} quotation(s)? This cannot be undone.`,
      async () => {
        const batch = firestore.batch();
        selectedIds.forEach((id) => {
          const ref = firestore.collection("quotes").doc(id);
          batch.delete(ref);
        });
        await batch.commit();
        setSelectedIds([]);
        showAlert(`${selectedIds.length} quotation(s) deleted successfully.`, "success", "Deleted");
      },
      "Delete Quotations",
      "warning"
    );
  };

  const handleApproveAdjustment = async (approval) => {
    if (processingApproval) return;
    setProcessingApproval(approval.id);
    try {
      // Calculate adjusted pricing
      const adjustedDerived = calculateAdjustedPricing(approval.originalDerived, approval.adjustmentAmount);
      
      // Update quote with adjusted pricing
      await firestore.collection("quotes").doc(approval.quoteId).update({
        derived: adjustedDerived,
        "meta.priceAdjustedAt": Date.now(),
        "meta.priceAdjustedBy": user.uid,
        "meta.priceAdjustedBySalesperson": approval.salespersonName,
        "meta.lastUpdatedAt": Date.now()
      });

      // Update approval request status
      await firestore.collection("priceAdjustmentRequests").doc(approval.id).update({
        status: "approved",
        approvedAt: Date.now(),
        approvedBy: user.uid
      });

      showAlert("Price adjustment approved and applied successfully.", "success", "Approved");
    } catch (error) {
      console.error("Error approving adjustment:", error);
      showAlert("Failed to approve adjustment. Please try again.", "error", "Error");
    } finally {
      setProcessingApproval(null);
    }
  };

  const handleRejectAdjustment = async (approval) => {
    if (processingApproval) return;
    showConfirm(
      `Reject price adjustment request for ${approval.customerName}?`,
      async () => {
        setProcessingApproval(approval.id);
        try {
          await firestore.collection("priceAdjustmentRequests").doc(approval.id).update({
            status: "rejected",
            rejectedAt: Date.now(),
            rejectedBy: user.uid
          });
          showAlert("Price adjustment request rejected.", "success", "Rejected");
        } catch (error) {
          console.error("Error rejecting adjustment:", error);
          showAlert("Failed to reject adjustment. Please try again.", "error", "Error");
        } finally {
          setProcessingApproval(null);
        }
      },
      "Reject Adjustment",
      "warning"
    );
  };

  return (
    <div className="quotation-system">
      <TopBar />
      <Modal {...modal} onClose={hideModal} />
      
      {/* Pending Approvals Section */}
      {pendingApprovals.length > 0 && (
        <div className="elegant-container fade-in-up" style={{ marginBottom: "2rem" }}>
          <div className="elegant-card" style={{ border: "2px solid var(--accent-gold)", background: "linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)" }}>
            <h2 className="elegant-title" style={{ fontSize: "1.5rem", marginBottom: "1rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <FiClock style={{ color: "var(--accent-gold)" }} />
              Pending Price Adjustment Approvals ({pendingApprovals.length})
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              {pendingApprovals.map((approval) => {
                // Calculate amount before and after adjustment
                const amountBefore = approval.originalDerived?.totalBeforeSubsidy || 0;
                const adjustedDerived = calculateAdjustedPricing(approval.originalDerived, approval.adjustmentAmount);
                const amountAfter = adjustedDerived.totalBeforeSubsidy || 0;
                const phoneData = approval.customerPhone ? formatPhoneNumber(approval.customerPhone) : null;
                
                return (
                  <div key={approval.id} style={{ 
                    padding: "1.5rem", 
                    background: "white", 
                    borderRadius: "8px", 
                    border: "1px solid var(--neutral-200)",
                    marginBottom: "1rem"
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "1.5rem" }}>
                      <div style={{ flex: 1, minWidth: "280px" }}>
                        <div style={{ 
                          display: "grid", 
                          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
                          gap: "1rem",
                          marginBottom: "1rem"
                        }}>
                          <div>
                            <div style={{ fontSize: "0.75rem", color: "var(--neutral-500)", marginBottom: "0.25rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                              Client Name
                            </div>
                            <div style={{ fontWeight: "600", fontSize: "1.125rem", color: "var(--neutral-900)" }}>
                              {approval.customerName}
                            </div>
                          </div>
                          
                          <div>
                            <div style={{ fontSize: "0.75rem", color: "var(--neutral-500)", marginBottom: "0.25rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                              Phone Number
                            </div>
                            <div style={{ fontWeight: "500", fontSize: "1rem", color: "var(--neutral-700)" }}>
                              {phoneData ? (
                                <a
                                  href={`tel:${phoneData.tel}`}
                                  style={{
                                    color: "var(--accent-cyan)",
                                    textDecoration: "none",
                                    fontWeight: "500"
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.textDecoration = "underline";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.textDecoration = "none";
                                  }}
                                >
                                  {phoneData.display}
                                </a>
                              ) : (
                                <span style={{ color: "var(--neutral-400)" }}>Not available</span>
                              )}
                            </div>
                          </div>
                          
                          <div>
                            <div style={{ fontSize: "0.75rem", color: "var(--neutral-500)", marginBottom: "0.25rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                              System Size
                            </div>
                            <div style={{ fontWeight: "600", fontSize: "1rem", color: "var(--neutral-900)" }}>
                              {approval.capacityKw ? `${approval.capacityKw} kW` : "N/A"}
                            </div>
                          </div>
                          
                          <div>
                            <div style={{ fontSize: "0.75rem", color: "var(--neutral-500)", marginBottom: "0.25rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                              Quote Number
                            </div>
                            <div style={{ fontWeight: "600", fontSize: "1rem", color: "var(--neutral-700)" }}>
                              {approval.quoteNo}
                            </div>
                          </div>
                        </div>
                        
                        <div style={{ 
                          padding: "1rem", 
                          background: "linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(99, 102, 241, 0.05) 100%)",
                          borderRadius: "6px",
                          border: "1px solid var(--neutral-200)",
                          marginBottom: "1rem"
                        }}>
                          <div style={{ 
                            display: "grid", 
                            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", 
                            gap: "1rem"
                          }}>
                            <div>
                              <div style={{ fontSize: "0.75rem", color: "var(--neutral-500)", marginBottom: "0.25rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                Amount Before
                              </div>
                              <div style={{ fontWeight: "700", fontSize: "1.25rem", color: "var(--neutral-900)" }}>
                                {formatINR(amountBefore)}
                              </div>
                            </div>
                            
                            <div>
                              <div style={{ fontSize: "0.75rem", color: "var(--neutral-500)", marginBottom: "0.25rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                Adjustment
                              </div>
                              <div style={{ 
                                fontWeight: "700", 
                                fontSize: "1.25rem", 
                                color: approval.adjustmentAmount >= 0 ? "var(--success)" : "var(--error)"
                              }}>
                                {approval.adjustmentAmount >= 0 ? "+" : ""}{formatINR(approval.adjustmentAmount)}
                              </div>
                            </div>
                            
                            <div>
                              <div style={{ fontSize: "0.75rem", color: "var(--neutral-500)", marginBottom: "0.25rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                Amount After
                              </div>
                              <div style={{ fontWeight: "700", fontSize: "1.25rem", color: "var(--accent-cyan)" }}>
                                {formatINR(amountAfter)}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div style={{ fontSize: "0.875rem", color: "var(--neutral-600)" }}>
                          Requested by: <strong>{approval.salespersonName}</strong>
                        </div>
                      </div>
                      
                      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignSelf: "flex-start" }}>
                        <button
                          onClick={() => handleApproveAdjustment(approval)}
                          disabled={processingApproval === approval.id}
                          className="btn-elegant"
                          style={{
                            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                            color: "white",
                            border: "none",
                            padding: "0.75rem 1.5rem",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            minHeight: "44px",
                            fontSize: "1rem",
                            fontWeight: "600"
                          }}
                        >
                          <FiCheck />
                          {processingApproval === approval.id ? "Processing..." : "Approve"}
                        </button>
                        <button
                          onClick={() => handleRejectAdjustment(approval)}
                          disabled={processingApproval === approval.id}
                          className="btn-elegant"
                          style={{
                            background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                            color: "white",
                            border: "none",
                            padding: "0.75rem 1.5rem",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            minHeight: "44px",
                            fontSize: "1rem",
                            fontWeight: "600"
                          }}
                        >
                          <FiX />
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="elegant-container fade-in-up">
        <div className="elegant-card">
          <h1 className="elegant-title">Quotation Management</h1>
          <p className="elegant-subtitle">View and manage all quotations across your organization</p>
          
          <div className="search-elegant">
            <input
              type="text"
              className="form-input-elegant"
              placeholder="Search by quote no., customer, phone, salesperson..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          
          <div style={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center", 
            marginBottom: "1.5rem",
            flexWrap: "wrap",
            gap: "1rem"
          }}>
            <span style={{ color: "var(--neutral-600)", fontWeight: "600", fontSize: "0.9375rem" }}>
              {filtered.length} {filtered.length === 1 ? "quotation" : "quotations"}
            </span>
            {selectedIds.length > 0 && (
              <button
                onClick={deleteSelected}
                className="btn-elegant btn-elegant-danger"
                style={{ 
                  padding: "0.75rem 1.5rem", 
                  minHeight: "48px",
                  minWidth: "180px"
                }}
              >
                Delete {selectedIds.length} selected
              </button>
            )}
          </div>
          
          <div style={{ overflowX: "auto" }}>
            <table className="table-elegant">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      onChange={toggleAll}
                      checked={
                        filtered.length > 0 &&
                        filtered.every((q) => selectedIds.includes(q.meta.id))
                      }
                      style={{ 
                        minWidth: "24px", 
                        minHeight: "24px",
                        width: "24px",
                        height: "24px",
                        cursor: "pointer",
                        touchAction: "manipulation"
                      }}
                    />
                  </th>
                  <th align="left">Quote No</th>
                  <th align="left">Customer</th>
                  <th align="left">Phone</th>
                  <th align="left">Capacity</th>
                  <th align="left">Sales</th>
                  <th align="left">Date</th>
                  <th align="left">PDF</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((q) => (
                  <tr key={q.meta.id}>
                    <td data-label="">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(q.meta.id)}
                        onChange={() => toggleOne(q.meta.id)}
                        style={{ 
                          minWidth: "24px", 
                          minHeight: "24px",
                          width: "24px",
                          height: "24px",
                          cursor: "pointer",
                          touchAction: "manipulation"
                        }}
                      />
                    </td>
                    <td data-label="Quote No">{q.meta.quoteNo}</td>
                    <td data-label="Customer">{q.customer.name}</td>
                    <td data-label="Phone" style={{ whiteSpace: "nowrap" }}>
                      {(() => {
                        const phoneData = formatPhoneNumber(q.customer.phone);
                        if (phoneData.tel) {
                          return (
                            <a
                              href={`tel:${phoneData.tel}`}
                              style={{
                                color: "var(--accent-cyan)",
                                textDecoration: "none",
                                fontWeight: "500",
                                whiteSpace: "nowrap",
                                display: "inline-block"
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.textDecoration = "underline";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.textDecoration = "none";
                              }}
                            >
                              {phoneData.display}
                            </a>
                          );
                        }
                        return <span style={{ color: "var(--neutral-400)" }}>{phoneData.display}</span>;
                      })()}
                    </td>
                    <td data-label="Capacity">{q.derived.capacityKw} kW</td>
                    <td data-label="Sales">{q.meta.salespersonName}</td>
                    <td data-label="Date">{(() => {
                      const date = new Date(q.meta.createdAt);
                      const day = date.getDate().toString().padStart(2, '0');
                      const month = date.toLocaleDateString("en-GB", { month: "long" });
                      const year = date.getFullYear();
                      const hours = date.getHours().toString().padStart(2, '0');
                      const minutes = date.getMinutes().toString().padStart(2, '0');
                      return `${day} ${month} ${year}, ${hours}:${minutes}`;
                    })()}</td>
                    <td data-label="PDF">
                      <button 
                        onClick={() => regenerate(q)}
                        className="btn-elegant btn-elegant-accent"
                        style={{ padding: "0.5rem 1rem", fontSize: "0.875rem", width: "100%", minHeight: "44px" }}
                      >
                        Open PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}


